"use client";

import { useLayoutEffect } from "react";
import { useDashboardTheme } from "@/components/dashboard/DashboardThemeProvider";
import { useThemeTokens } from "./hooks";
import { APP_BG_DARK, APP_BG_LIGHT } from "@/lib/constants/app-theme";
import { getBrandCssVariablesFromAccentPrimary } from "@/lib/theme/brand-css-variables";

/**
 * Dynamically updates browser theme color meta tags based on current theme mode
 * This affects the top and bottom browser bars on mobile devices
 * Also sets CSS variables for scrollbar colors based on selected palette
 */
export const ThemeColorMeta = () => {
  const { mode, colorPalette, previewPalette } = useDashboardTheme();
  const theme = useThemeTokens();

  useLayoutEffect(() => {
    // Single source of truth - matches lib/constants/app-theme.ts
    const themeColors = {
      dark: APP_BG_DARK,
      light: APP_BG_LIGHT,
    };

    const color = themeColors[mode];

    // Update or create theme-color meta tag for Android Chrome
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute("content", color);

    // Update or create apple-mobile-web-app-status-bar-style for iOS Safari
    let appleStatusBar = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (!appleStatusBar) {
      appleStatusBar = document.createElement("meta");
      appleStatusBar.setAttribute("name", "apple-mobile-web-app-status-bar-style");
      document.head.appendChild(appleStatusBar);
    }
    // For iOS: "black-translucent" shows white text on colored background
    // "black" shows black text on white background, "default" uses system default
    appleStatusBar.setAttribute(
      "content",
      mode === "dark" ? "black-translucent" : "default",
    );

    // Update or create msapplication-navbutton-color for Windows Phone (legacy support)
    let msNavButton = document.querySelector(
      'meta[name="msapplication-navbutton-color"]',
    );
    if (!msNavButton) {
      msNavButton = document.createElement("meta");
      msNavButton.setAttribute("name", "msapplication-navbutton-color");
      document.head.appendChild(msNavButton);
    }
    msNavButton.setAttribute("content", color);

    // Also set msapplication-TileColor for Windows tiles
    let msTileColor = document.querySelector(
      'meta[name="msapplication-TileColor"]',
    );
    if (!msTileColor) {
      msTileColor = document.createElement("meta");
      msTileColor.setAttribute("name", "msapplication-TileColor");
      document.head.appendChild(msTileColor);
    }
    msTileColor.setAttribute("content", color);

    // Set CSS variables for scrollbar and selection colors based on palette
    const root = document.documentElement;
    const brandCssVariables = getBrandCssVariablesFromAccentPrimary(
      theme.accent.primary,
    );

    for (const [key, value] of Object.entries(brandCssVariables)) {
      root.style.setProperty(key, value);
    }
  }, [mode, colorPalette, previewPalette, theme.accent.primary]);

  // This component doesn't render anything
  return null;
};

